// Renderizador de templates de texto do encaminhamento.
//
// Marcacao canonica (a que o editor do painel grava):
//   **negrito**   _italico_   ~~riscado~~   __sublinhado__
// Saida por destino:
//   - telegram: HTML (parse_mode=HTML) com <b> <i> <s> <u>, valores escapados;
//   - whatsapp: *negrito* _italico_ ~riscado~ (sem sublinhado: vira texto puro);
//   - plain: marcacao removida.
//
// Variaveis: {title} {priceText} {price} {discountedPrice} {originalPrice}
// {discountPercent} {savings} {coupon} {affiliateUrl} {provider}. Uma linha cujas
// variaveis estao TODAS ausentes some inteira (com o rotulo junto), sem deixar
// linha em branco sobrando. Valores nunca sao interpretados como marcacao.
import { formatBrl } from "./offer.js";
import { joinCollapsed } from "./terms.js";
export const TEMPLATE_VARIABLES = [
    "title",
    "priceText",
    "price",
    "discountedPrice",
    "originalPrice",
    "discountPercent",
    "savings",
    "coupon",
    "affiliateUrl",
    "provider",
];
export const MAX_TEMPLATE_LENGTH = 2000;
export const PROVIDER_LABELS = {
    amazon: "Amazon",
    mercadolivre: "Mercado Livre",
    shopee: "Shopee",
    magalu: "Magalu",
    aliexpress: "AliExpress",
    shein: "SHEIN",
    awin: "Awin",
};
const VARIABLE_SET = new Set(TEMPLATE_VARIABLES);
const VAR_RE = /\{([A-Za-z]+)\}/g;
// Caractere de uso privado do Unicode: nunca aparece em texto de oferta.
const SENTINEL = "\uE000";
const SENTINEL_RE = /\uE000(\d+)\uE000/g;
/** Monta as variaveis a partir da oferta extraida + link convertido. */
export function buildTemplateVars(offer, affiliateUrl, providerCode) {
    const price = offer.priceCents;
    const original = offer.originalPriceCents;
    const hasDiscount = price !== null && original !== null && original > price;
    const priceText = price === null
        ? null
        : hasDiscount
            ? `de ${formatBrl(original)} por ${formatBrl(price)}`
            : `por ${formatBrl(price)}`;
    return {
        title: offer.title,
        priceText,
        price: price === null ? null : formatBrl(price),
        discountedPrice: hasDiscount ? formatBrl(price) : null,
        originalPrice: hasDiscount ? formatBrl(original) : null,
        discountPercent: offer.discountPercent === null ? null : `${offer.discountPercent}%`,
        savings: hasDiscount ? formatBrl(original - price) : null,
        coupon: offer.coupon,
        affiliateUrl,
        provider: providerCode === null ? null : (PROVIDER_LABELS[providerCode] ?? providerCode),
    };
}
function isPresent(value) {
    return typeof value === "string" && value.trim().length > 0;
}
/** Variaveis conhecidas citadas numa linha. */
function knownVarsIn(line) {
    return [...line.matchAll(VAR_RE)].map((m) => m[1]).filter((name) => VARIABLE_SET.has(name));
}
export function escapeHtml(value) {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
const BOLD_RE = /\*\*(?=\S)([^\n]*?\S)\*\*/g;
const UNDERLINE_RE = /__(?=\S)([^\n]*?\S)__/g;
const STRIKE_RE = /~~(?=\S)([^\n]*?\S)~~/g;
const ITALIC_RE = /(^|[^\p{L}\p{N}_])_(?=\S)([^\n_]*?\S)_(?![\p{L}\p{N}_])/gmu;
function formatMarkup(text, target) {
    if (target === "telegram") {
        return escapeHtml(text)
            .replace(UNDERLINE_RE, "<u>$1</u>")
            .replace(BOLD_RE, "<b>$1</b>")
            .replace(STRIKE_RE, "<s>$1</s>")
            .replace(ITALIC_RE, "$1<i>$2</i>");
    }
    if (target === "whatsapp") {
        return text.replace(UNDERLINE_RE, "$1").replace(BOLD_RE, "*$1*").replace(STRIKE_RE, "~$1~");
    }
    return text
        .replace(UNDERLINE_RE, "$1")
        .replace(BOLD_RE, "$1")
        .replace(STRIKE_RE, "$1")
        .replace(ITALIC_RE, "$1$2");
}
const TAG_TOKEN_RE = /<(\/?)(b|i|s|u)>/g;
/** As tags b/i/s/u geradas estao bem aninhadas? (o Telegram rejeita HTML cruzado) */
export function isWellNestedHtml(html) {
    const stack = [];
    for (const m of html.matchAll(TAG_TOKEN_RE)) {
        if (m[1] === "") {
            stack.push(m[2]);
        }
        else if (stack.pop() !== m[2]) {
            return false;
        }
    }
    return stack.length === 0;
}
function formatForTarget(text, target) {
    const formatted = formatMarkup(text, target);
    // Marcacao sobreposta (ex.: **a __b** c__) geraria HTML invalido e o Telegram
    // recusaria a mensagem inteira: cai para texto sem formatacao, escapado.
    return target === "telegram" && !isWellNestedHtml(formatted)
        ? escapeHtml(formatMarkup(text, "plain"))
        : formatted;
}
/** Renderiza o template para o destino. */
export function renderTemplate(template, vars, target) {
    const values = [];
    const lines = [];
    for (const line of template.replace(new RegExp(SENTINEL, "g"), "").split(/\r?\n/)) {
        const names = knownVarsIn(line);
        if (names.length > 0 && names.every((name) => !isPresent(vars[name]))) {
            continue;
        }
        lines.push(line.replace(VAR_RE, (whole, name) => {
            if (!VARIABLE_SET.has(name)) {
                return whole;
            }
            values.push(vars[name] ?? "");
            return `${SENTINEL}${values.length - 1}${SENTINEL}`;
        }));
    }
    const formatted = formatForTarget(joinCollapsed(lines), target);
    return formatted.replace(SENTINEL_RE, (_, index) => {
        const value = values[Number(index)] ?? "";
        return target === "telegram" ? escapeHtml(value) : value;
    });
}
/** Converte texto puro de origem para o destino sem template (so escapa no Telegram HTML). */
export function plainToTarget(text, target) {
    return target === "telegram" ? escapeHtml(text) : text;
}
//# sourceMappingURL=template.js.map